import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { REPORT_GROUPS, REPORTS } from "@/components/reports/catalog";
import { MODULE_LABELS, PERM_LABELS } from "@/lib/api";

const SCREEN_LABELS = { ...MODULE_LABELS, painel: "Painel", planos: "Planos e assinatura", venda_ticket: "Venda ticket", consulta_itens: "Consulta itens venda" };
export const SCREEN_GROUPS = [
  { group: "Vendas", items: ["painel", "pdv", "venda_ticket", "consulta_itens", "caixa", "encomendas"] },
  { group: "Atendimento", items: ["mesas", "comandas", "delivery", "fidelidade", "producao", "tickets", "estacionamento", "cardapio"] },
  { group: "Cadastros", items: ["produtos", "estoque", "clientes"] },
  { group: "Gestão", items: ["relatorios", "fiscal", "folha", "planos"] },
];

function CheckGroup({ title, items, labels, checked, onToggle, onAll, prefix }) {
  const all = items.every((k) => checked.includes(k));
  return (
    <div className="rounded-lg border p-3 space-y-2">
      <label className="flex items-center gap-2 text-sm font-semibold"><Checkbox checked={all} onCheckedChange={(v) => onAll(items, v)} data-testid={`${prefix}-group-${title}`} />{title}</label>
      <div className="grid sm:grid-cols-2 gap-1.5 pl-6">
        {items.map((k) => <label key={k} className="flex items-center gap-2 text-sm"><Checkbox checked={checked.includes(k)} onCheckedChange={() => onToggle(k)} data-testid={`${prefix}-${k}`} />{labels[k] || k}</label>)}
      </div>
    </div>
  );
}

function useList(edit, setEdit, key, fallback) {
  const list = edit[key] ?? fallback;
  const toggle = (k) => setEdit((x) => ({ ...x, [key]: list.includes(k) ? list.filter((y) => y !== k) : [...list, k] }));
  const setAll = (keys, v) => setEdit((x) => ({ ...x, [key]: v ? [...new Set([...list, ...keys])] : list.filter((y) => !keys.includes(y)) }));
  return [list, toggle, setAll];
}

function ActionSwitches({ edit, setEdit, defaults }) {
  const base = defaults.roles?.[edit.role] || [];
  const ov = edit.permission_overrides || {};
  const on = (p) => (p in ov ? ov[p] : base.includes(p));
  const toggle = (p, v) => {
    const next = { ...ov };
    if (v === base.includes(p)) delete next[p]; else next[p] = v;
    setEdit((x) => ({ ...x, permission_overrides: next }));
  };
  return (
    <div className="grid sm:grid-cols-2 gap-2" data-testid="user-permissions">
      {Object.entries(PERM_LABELS).map(([p, l]) => (
        <label key={p} className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm">{l}<Switch checked={on(p)} onCheckedChange={(v) => toggle(p, v)} data-testid={`user-perm-${p}`} /></label>
      ))}
    </div>
  );
}

export function UserPermissions({ edit, setEdit, defaults, stores }) {
  const roleAreas = defaults.areas?.[edit.role] || [];
  const [screens, toggleScreen, allScreens] = useList(edit, setEdit, "screen_areas", roleAreas);
  const [reports, toggleReport, allReports] = useList(edit, setEdit, "report_access", REPORTS.map((r) => r.k));
  const [storeList, toggleStore, allStores] = useList(edit, setEdit, "store_access", stores.map((s) => s.id));
  const reportLabels = Object.fromEntries(REPORTS.map((r) => [r.k, r.label]));
  const storeLabels = Object.fromEntries(stores.map((s) => [s.id, s.name]));
  const custom = edit.screen_areas != null;
  return (
    <Tabs defaultValue="telas">
      <TabsList className="flex-wrap h-auto">
        <TabsTrigger value="telas" data-testid="perm-tab-telas">Telas</TabsTrigger>
        <TabsTrigger value="relatorios" data-testid="perm-tab-relatorios">Relatórios</TabsTrigger>
        <TabsTrigger value="unidades" data-testid="perm-tab-unidades">Empresas / unidades</TabsTrigger>
        <TabsTrigger value="acoes" data-testid="perm-tab-acoes">Ações</TabsTrigger>
      </TabsList>
      <TabsContent value="telas" className="mt-3 space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span data-testid="screens-mode">{custom ? "Telas personalizadas para este usuário" : "Usando as telas padrão do nível de acesso"}</span>
          {custom && <Button size="sm" variant="ghost" onClick={() => setEdit((x) => ({ ...x, screen_areas: null }))} data-testid="screens-reset-btn">Voltar ao padrão do nível</Button>}
        </div>
        {SCREEN_GROUPS.map((g) => <CheckGroup key={g.group} title={g.group} items={g.items} labels={SCREEN_LABELS} checked={screens} onToggle={toggleScreen} onAll={allScreens} prefix="perm-screen" />)}
        <p className="text-xs text-muted-foreground">Configurações pessoais (senha, aparência) ficam sempre disponíveis.</p>
      </TabsContent>
      <TabsContent value="relatorios" className="mt-3 space-y-2">
        <p className="text-xs text-muted-foreground">Os relatórios só aparecem se a tela Relatórios estiver liberada.</p>
        {REPORT_GROUPS.map((g) => <CheckGroup key={g.group} title={g.group} items={g.items.map((r) => r.k)} labels={reportLabels} checked={reports} onToggle={toggleReport} onAll={allReports} prefix="perm-report" />)}
      </TabsContent>
      <TabsContent value="unidades" className="mt-3">
        <CheckGroup title="Empresas" items={stores.map((s) => s.id)} labels={storeLabels} checked={storeList} onToggle={toggleStore} onAll={allStores} prefix="perm-store" />
      </TabsContent>
      <TabsContent value="acoes" className="mt-3"><ActionSwitches edit={edit} setEdit={setEdit} defaults={defaults} /></TabsContent>
    </Tabs>
  );
}
